<template>
  <div
    v-if="!isFullscreen"
    :data-role="fixed ? 'fixed-toolbar' : 'inline-toolbar'"
    :data-transparent-toolbar="transparent"
    :style="customStyles"
    :class="[
      'v-toolbar__wrapper',
      {
        '--mobile-viewport': isBuilderContext() && isMobile,
        '--desktop-viewport': isBuilderContext() && isDesktop
      }
    ]"
  >
    <div
      :class="[
        {
          '--transparent': transparent,
          '--dark': isDarkBackground,
          '--flat': flat,
          '--inline': inline,
          '--fixed top-0 left-0': fixed && !inline
        },
        'v-toolbar'
      ]"
      id="toolbar"
      :style="
        useAccountStyles
          ? {
              '--toolbar-background-color': toolbar.background_color
            }
          : {}
      "
    >
      <div class="v-toolbar__container" ref="toolbarContainer">
        <div class="v-toolbar__content">
          <div class="v-toolbar__left">
            <v-button
              v-if="back"
              class="v-toolbar__button"
              round
              ghost
              data-cy="back"
              data-role="back"
              @click="attemptBack"
            >
              <v-icon icon="arrow_back" small rtl-flip />
            </v-button>

            <v-button
              v-if="withMenu && !isDesktop"
              round
              ghost
              class="v-toolbar__button"
              data-cy="menu"
              @click="toggleSidebar"
            >
              <v-icon icon="menu" small />
            </v-button>

            <slot name="left" />

            <template v-if="showLeftLogo">
              <v-link class="d-flex align-items-center" to="/">
                <toolbar-logo
                  v-if="logo"
                  :logo="toolbar.logo"
                  :fallback-text="app.name"
                  :styles="styles"
                />
              </v-link>
            </template>
          </div>

          <div class="v-toolbar__center">
            <horizontal-toolbar-navigation
              v-if="
                withNavigation && isDesktop && !transparent && !isTabbarShown
              "
            />

            <template v-if="showCenteredLogo">
              <v-link class="d-flex align-items-center" to="/">
                <toolbar-logo
                  v-if="logo"
                  :logo="toolbar.logo"
                  :fallback-text="app.name"
                  :styles="styles"
                />
              </v-link>
            </template>

            <slot name="center" />
          </div>

          <div class="v-toolbar__right">
            <div id="v-toolbar-right-portal"></div>
            <slot name="right" />

            <v-button
              v-if="withMenu && isDesktop"
              round
              ghost
              @click="toggleSidebar"
              class="v-toolbar__button"
            >
              <v-icon icon="menu" />
            </v-button>
          </div>
        </div>

        <slot name="append" />
      </div>
    </div>

    <div v-if="showToolbarSpacer" class="v-toolbar__spacer">
      <div class="v-toolbar__spacer--padding">
        <div class="v-toolbar__spacer--border" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { isBuilderContext } from "@/lib/utils"
import { generateStyleVariables, isCssZeroValue } from "@/lib/styles-utils"
import { getDesktopKey } from "@shared/admin-app-constants"

import enumValidator from "@/lib/validators/enum-validator"
import { isDarkTextColor, rgbToHex } from "@shared/determine-text-color"
import { withRatio } from "@shared/block-ratio"

import ToolbarLogo, { DEFAULT_STYLES } from "@/components/toolbar/Logo"
import HorizontalToolbarNavigation from "@/components/horizontal-toolbar/Navigation"

export default {
  components: {
    ToolbarLogo,
    HorizontalToolbarNavigation
  },

  props: {
    transparent: { type: Boolean, default: false },
    logo: { type: Boolean, default: true },
    fixed: { type: Boolean, default: false },
    back: { type: Boolean, default: true },
    align: {
      type: String,
      default: null,
      validator: enumValidator(["left", "center"])
    },
    emitOnExitAttempt: { type: Boolean, default: false },
    flat: { type: Boolean, default: false },
    useAccountStyles: { type: Boolean, default: false },
    withMenu: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    withNavigation: { type: Boolean, default: false },
    inline: { type: Boolean, default: false },
    allowOverlappingContent: { type: Boolean, default: false }
  },

  data() {
    return {
      isDarkBackground: false
    }
  },

  computed: {
    ...mapState(["global"]),
    ...mapState("videos", ["isFullscreen"]),
    ...mapState("responsive", ["isDesktop", "isMobile"]),
    ...mapGetters("tabbar", ["isTabbarShown"]),

    app() {
      return this.global?.app || {}
    },

    toolbar() {
      return this.global?.toolbar || {}
    },

    customStyles() {
      return this.useAccountStyles
        ? generateStyleVariables(this.toolbar.styles)
        : {}
    },

    styles() {
      return {
        ...DEFAULT_STYLES,
        height: withRatio(50, this.toolbar.logo_height_ratio, { unit: "%" })
      }
    },

    hasSlotData() {
      return !!this.$slots.left || !!this.$slots.center
    },

    showLeftLogo() {
      return this.isDesktop && !this.transparent && !this.hasSlotData
    },

    showCenteredLogo() {
      return !this.isDesktop && !this.transparent && !this.hasSlotData
    },

    showToolbarSpacer() {
      if (!this.fixed || this.transparent || this.inline) return false

      return this.allowOverlappingContent
        ? !this.hasNonZeroToolbarMargin()
        : true
    }
  },

  watch: {
    toolbar: {
      immediate: true,
      handler() {
        this.setupDarkTextColor()
      }
    },

    transparent() {
      this.setupDarkTextColor()
    }
  },

  methods: {
    isBuilderContext,

    ...mapMutations("tabbar", ["toggleSidebar"]),

    goBack() {
      this.$router.back()
    },

    attemptBack() {
      if (this.pageId && EventBus.hasListener(EVENTS.PAGE_EXIT_ATTEMPTED)) {
        EventBus.$emit(EVENTS.PAGE_EXIT_ATTEMPTED, {
          pageId: this.pageId
        })
      } else {
        this.goBack()
      }
    },

    setupDarkTextColor() {
      this.$nextTick(() => {
        if (!this.useAccountStyles) return false

        let backgroundColor = null

        if (this.$refs.toolbarContainer) {
          const style = getComputedStyle(this.$refs.toolbarContainer)
          backgroundColor = style.getPropertyValue("background-color")
        }

        backgroundColor = backgroundColor
          ? rgbToHex(backgroundColor)
          : this.toolbar?.background_color

        this.isDarkBackground = !isDarkTextColor(backgroundColor)
      })
    },

    hasNonZeroToolbarMargin() {
      const styles = this.toolbar?.styles || {}
      const margin = this.isDesktop
        ? styles[getDesktopKey("margin")] ?? styles.margin
        : styles.margin

      if (margin == null || margin === "") return false

      const values = String(margin)
        .trim()
        .split(/\s+/)
      if (!values.length) return false

      return values.some(value => !isCssZeroValue(value))
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
$content--max-width--spacer: 224px;

@mixin toolbar-desktop {
  #toolbar.v-toolbar:not(.--transparent):not(.--inline) .v-toolbar__container {
    margin: var(--mvt-margin--desktop, var(--mvt-margin));
    border-width: var(--mvt-border-width--desktop, var(--mvt-border-width));
    border-style: var(--mvt-border-style--desktop, var(--mvt-border-style));
    border-color: var(--mvt-border-color--desktop, var(--mvt-border-color));
    border-radius: var(--mvt-corner-radius--desktop, var(--mvt-corner-radius));
    box-shadow: var(--mvt-shadow--desktop, var(--mvt-shadow));
    max-width: var(
      --mvt-navigation-max-width--desktop,
      var(--mvt-navigation-max-width, 100%)
    );
    background-color: var(
      --mvt-color--desktop,
      var(--mvt-color, var(--toolbar-background-color))
    );

    .v-toolbar__content {
      padding: var(--mvt-padding--desktop, var(--mvt-padding, 0 $size--2));
      gap: var(--mvt-element-spacing--desktop, var(--mvt-element-spacing));
      max-width: var(
        --mvt-content-max-width--desktop,
        var(
          --mvt-content-max-width,
          calc($content--max-width + $content--max-width--spacer)
        )
      );
    }
  }

  .v-toolbar__spacer {
    padding: var(--mvt-margin--desktop, var(--mvt-margin));

    .v-toolbar__spacer--padding {
      padding: var(--mvt-padding--desktop, var(--mvt-padding, 0 $size--2));
    }

    .v-toolbar__spacer--border {
      padding: var(--mvt-border-width--desktop, var(--mvt-border-width));
    }
  }
}

.v-toolbar__wrapper {
  position: relative;

  #toolbar.v-toolbar:not(.--transparent):not(.--inline) {
    .v-toolbar__container {
      margin: var(--mvt-margin);
      border-width: var(--mvt-border-width);
      border-style: var(--mvt-border-style);
      border-color: var(--mvt-border-color);
      border-radius: var(--mvt-corner-radius);
      box-shadow: var(--mvt-shadow);
      max-width: var(--mvt-navigation-max-width, 100%);
      background-color: var(--mvt-color, var(--toolbar-background-color));

      .v-toolbar__content {
        gap: var(--mvt-element-spacing);
        padding: var(--mvt-padding, 0 $size--2);
        max-width: var(
          --mvt-content-max-width,
          calc($content--max-width + $content--max-width--spacer)
        );
      }
    }
  }

  .v-toolbar__spacer {
    padding: var(--mvt-margin);

    .v-toolbar__spacer--padding {
      padding: var(--mvt-padding, 0 $size--2);
    }

    .v-toolbar__spacer--border {
      padding: var(--mvt-border-width);
    }
  }

  &.--desktop-viewport {
    @include toolbar-desktop;
  }

  @media screen and (min-width: $breakpoint--xl) {
    &:not(.--mobile-viewport) {
      @include toolbar-desktop;
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

:root {
  --toolbar-height: 3.5rem;

  @media screen and (min-width: $breakpoint--xl) {
    --toolbar-height: 4.5rem;
  }
}

.v-toolbar {
  display: flex;
  justify-content: center;
  background-clip: padding-box;
  user-select: none;
  position: relative;
  z-index: $toolbar--z-index;
  color: var(--toolbar-color, $color-ui--grey-90);
  font-weight: $font-weight--base;
  width: 100%;
  padding-top: env(safe-area-inset-top);

  .v-toolbar__container {
    box-sizing: content-box;
    width: 100%;
    min-height: var(--toolbar-height);

    .v-toolbar__content {
      box-sizing: border-box;
      width: 100%;
    }
  }

  &.--dark:not(.--transparent) {
    .v-toolbar__text {
      color: $color-ui--white;
    }

    .v-toolbar__button {
      color: $color-ui--white;
    }

    .v-toolbar__content {
      .v-button {
        color: $color-ui--white;

        &:hover {
          background-color: rgba(var(--primary-brand-color-rgb-vals), 0.2);
        }
      }
    }

    .horizontal-toolbar__item {
      &.--active {
        background-color: rgba(var(--primary-brand-color-rgb-vals), 0.2);
      }

      &:not(.--active) {
        color: $color-ui--white;
      }
    }
  }

  &.--fixed {
    position: fixed;
  }

  &.--flat {
    box-shadow: none;

    .v-toolbar__content {
      box-shadow: none !important;
    }
  }

  &.--inline {
    position: relative;
    box-shadow: none;
    background-color: transparent;
    padding-top: 0;
    margin: 0 auto;
    border: none;

    .v-toolbar__content {
      max-width: var(--container-width);
    }
  }

  &.--transparent {
    background: transparent;
    box-shadow: none;
    border: none;

    .v-toolbar__content {
      max-width: var(--container-width);
    }

    .v-toolbar__button {
      @include clickable(0.9);

      background: $color-ui--white;
      position: relative;
      top: $size--1;
      box-shadow: $box-shadow--medium;
      color: $color-ui--grey-70;
      border-radius: 100px;
      width: $size--9;
      height: $size--9;
      padding: 0 $size--4;

      &:last-child {
        margin-right: 0;
      }

      .v-loader {
        --mvt-loader-size: #{$size--6};
      }
    }

    .v-toolbar__left {
      .v-toolbar__button {
        margin-right: $size--2;
      }

      .v-toolbar__button:first-child {
        margin-left: 0;
      }
    }

    .v-toolbar__right {
      .v-toolbar__button {
        background: rgba($color-rgb--black, 0.2);
        color: $color-ui--white;
        box-shadow: none;
        margin-left: $size--2;

        &.--active {
          background: $color-ui--white;
          box-shadow: $box-shadow--medium;
          color: $color-ui--grey-70;
        }
      }
    }

    .v-toolbar__content {
      padding: 0 $size--2;
      max-width: $content--max-width;
    }
  }

  .v-toolbar__button {
    color: $color-ui--grey-80;
  }
}

@media screen and (min-width: $breakpoint--xl) {
  .v-toolbar:not(.--inline) {
    top: 0;
    z-index: $toolbar--z-index + 1;
  }

  .v-toolbar__wrapper:not(.--mobile-viewport) .v-toolbar:not(.--transparent) {
    .v-toolbar__left {
      padding-right: $size--8;

      .v-toolbar__button:last-child {
        margin-right: $size--5;
      }
    }
  }
}

.--dark-mode {
  .v-toolbar {
    &.--transparent {
      .v-toolbar__button {
        border: 2px solid white;
      }

      .v-toolbar__right {
        .v-toolbar__button {
          border: none;
          color: white;
        }
      }
    }
  }
}

.--with-left-sidebar {
  [data-role="fixed-toolbar"] .v-toolbar {
    margin-left: $tabbar--desktop-width;
    width: calc(100% - #{$tabbar--desktop-width});
  }
}

.v-toolbar__content {
  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  white-space: nowrap;
  margin: 0 auto;
  padding: 0 $size--1;

  & > div {
    min-height: $toolbar--height;
    display: flex;
    align-items: center;
  }

  .v-toolbar__left {
    justify-content: flex-start;
    max-width: 80%;

    > a {
      height: 100%;
    }
  }

  .v-toolbar__center {
    flex: 1 2 46%;
    align-items: center;
    justify-content: center;
    max-width: 80%;
    min-width: 0;

    > a {
      min-width: 0;
      overflow: hidden;
      height: 100%;
    }
  }

  .v-toolbar__right {
    display: flex;
    justify-content: flex-end;

    --mvt-loader-size: 1.25rem;
  }

  .v-toolbar__button {
    font-size: 1.35rem;
    color: $color-ui--grey-70;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 $size--5;
    height: 100%;
    margin: 0;
    border: none;

    .v-icon {
      transform: scale(1);
    }
  }

  .v-toolbar__text {
    @include overflow-ellipsis;

    font-weight: $font-weight--bold;
    font-size: 1.35rem;
  }

  @media screen and (max-width: ($breakpoint--xl - 0.02px)) {
    .v-toolbar__left,
    .v-toolbar__right {
      flex: 0 1 27%;
    }
  }

  @media screen and (min-width: $breakpoint--xl) {
    display: grid;
    grid-template-columns: minmax(min-content, 1fr) auto minmax(
        min-content,
        1fr
      );

    .v-toolbar__left {
      max-width: none;
    }

    .v-toolbar__center {
      flex: none;
      max-width: none;
    }

    .v-toolbar__right {
      min-width: 0;
    }
  }
}

.v-toolbar__spacer,
.v-toolbar__spacer--padding,
.v-toolbar__spacer--border {
  box-sizing: content-box;
  min-height: calc(var(--toolbar-height) + env(safe-area-inset-top));
}

.v-toolbar__button {
  @include clickable(0.9);
}

[dir="rtl"] {
  .v-toolbar {
    &:not(.--transparent) {
      .v-toolbar-logo,
      .v-toolbar__text {
        margin-right: $size--5;
        margin-left: 0;
      }
    }
  }
}
</style>
